import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { FileUp, Leaf, ShoppingBag, User, Menu, X, LogOut, LayoutDashboard, MessageCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, mediaUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import SocialLinks from "@/components/SocialLinks";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Buy Now" },
  { to: "/yoga", label: "Yoga" },
  { to: "/garbhsanskar", label: "Garbhsanskar" },
  { to: "/retreats", label: "Natural Retreat" },
  { to: "/appointments", label: "Appointments" },
  { to: "/track", label: "Track" },
  { to: "/about", label: "About Us" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState({ clinic_name: "Immortal Vedas", logo_url: "" });
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => {
        setBrand({ clinic_name: data.clinic_name || "Immortal Vedas", logo_url: data.logo_url || "" });
        setSettings(data);
      })
      .catch(() => {});
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide whitespace-nowrap transition-colors ${isActive ? "text-primary" : "text-foreground/70 hover:text-primary"}`;

  // "Immortal Vedas" → "IV", whatever the clinic renames itself to.
  const initials = brand.clinic_name.split(/\s+/).filter(Boolean).map((word) => word[0]).join("").slice(0, 3).toUpperCase();

  return (
    <>
    <header className="relative z-40 glass-nav border-b border-border">
      {/* gap-6 keeps the brand, the links and the actions from ever touching, and the
          row is allowed to be wider on very large screens so the full button label fits. */}
      <div className="max-w-7xl 2xl:max-w-[1440px] mx-auto flex items-center justify-between gap-3 sm:gap-6 px-4 sm:px-6 lg:px-10 h-16">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          {brand.logo_url ? (
            <img
              src={mediaUrl(brand.logo_url)}
              alt={brand.clinic_name}
              className="w-9 h-9 rounded-full object-contain bg-background"
              data-testid="brand-logo-img"
            />
          ) : (
            <span className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <Leaf size={18} />
            </span>
          )}
          <div className="leading-tight">
            {/* Below 480px the name becomes its initials, which is what buys the room for
                the "Upload Rx" label beside the avatar on a small phone. */}
            <div className="font-display text-xl whitespace-nowrap">
              <span className="min-[480px]:hidden">{initials}</span>
              <span className="hidden min-[480px]:inline">{brand.clinic_name}</span>
            </div>
            <div className="hidden xl:block text-[10px] tracking-[0.25em] uppercase text-secondary -mt-0.5 whitespace-nowrap">Ayurveda · Natural Products</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-3 xl:gap-5 shrink-0">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 p-1 rounded-full hover:bg-accent">
                  <Avatar className="h-8 w-8">
                    {user.profile_pic ? <AvatarImage src={mediaUrl(user.profile_pic)} /> : null}
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard size={14} className="mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/chat")} data-testid="menu-chat">
                  <MessageCircle size={14} className="mr-2" /> Consultation Chat
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    <User size={14} className="mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Button variant="ghost" onClick={() => nav("/login")} data-testid="nav-login">Login</Button>
            </div>
          )}

          {/* The one action every patient needs, whether signed in or not. It shortens to
              "Upload Rx" on phones and drops to the icon between 1024px and 1536px, where
              the page links need the room. */}
          <Button
            onClick={() => nav("/upload-prescription")}
            className="btn-pill bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-2.5 sm:px-3 shrink-0"
            data-testid="nav-upload-prescription"
          >
            <FileUp size={15} />
            {/* Shown from 360px — the very narrowest phones keep the icon alone. */}
            <span className="hidden min-[360px]:inline sm:hidden ml-1.5 whitespace-nowrap">Upload Rx</span>
            <span className="hidden sm:inline lg:hidden 2xl:inline ml-1.5 whitespace-nowrap">Upload Prescription</span>
          </Button>
          <Link to="/cart" className="relative p-2 hover:text-primary" data-testid="nav-cart">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-secondary text-secondary-foreground text-[10px] rounded-full px-1.5 py-0.5 font-bold" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>
          <SocialLinks settings={settings} compact className="hidden md:flex lg:hidden xl:flex mr-1" />

          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border bg-background px-6 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} onClick={() => setOpen(false)}>{l.label}</NavLink>
          ))}
          <Button
            onClick={() => { setOpen(false); nav("/upload-prescription"); }}
            className="btn-pill bg-primary text-primary-foreground hover:bg-primary/90 w-full mt-1"
            data-testid="nav-mobile-upload-prescription"
          >
            <FileUp size={15} className="mr-1.5" /> Upload Prescription
          </Button>
          {!user && (
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => { setOpen(false); nav("/login"); }}>Login</Button>
              <Button onClick={() => { setOpen(false); nav("/register"); }} className="bg-primary text-primary-foreground">Register</Button>
            </div>
          )}
        </div>
      )}
    </header>

      {/* Just under the header, right-aligned beneath Login — the only way into the
          Vaidya AI chat now that the widget's floating launcher is hidden. */}
      <div className="border-b border-border/60 bg-background/60">
        <div className="max-w-7xl 2xl:max-w-[1440px] mx-auto flex justify-end px-6 lg:px-10 py-2">
          <button
            type="button"
            onClick={() => window.VaidyaAI?.open()}
            aria-label="Open Vaidya AI, the Ayurveda assistant"
            className="btn-pill inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 h-8 sm:h-9 px-3 sm:px-4 text-xs sm:text-sm font-medium shadow-[0_10px_24px_-18px_rgba(33,83,52,0.8)] transition-transform hover:-translate-y-0.5"
            data-testid="nav-vaidya-ai"
          >
            <span aria-hidden="true" className="text-base leading-none">&#x1F549;&#xFE0F;</span>
            <span className="whitespace-nowrap">Vaidya AI</span>
          </button>
        </div>
      </div>
    </>
  );
}
